import 'moment-timezone';
import moment from "moment"
import { IUserModel } from "./UserModel";;
import { ICompanyModel } from "./CompanyModel";
import { GetValue, IgnoreTimeZoneApiDate } from "../../Helpers/helper";
import { ICompanyUserRoleViewModel } from '../../viewModels/CompanyUserRoleViewModel';
import { PermissionsType } from '../../Hooks/isUserAuthorized';

export interface IUserCompanyPermissionModel {
    Id: number;
    CompanyId: number;
    UserId: number;
    IsAdmin?: boolean;
    IsDirector?: boolean;
    IsBidder?: boolean;
    IsGuest?: boolean;
    Created?: Date;
    CreatedById: number;
    Modified?: Date;
    IsActive?: boolean;

    SetIsActive: (value: boolean) => IUserCompanyPermissionModel;
    SetIsAdmin: (value: boolean) => IUserCompanyPermissionModel;
    SetIsDirector: (value: boolean) => IUserCompanyPermissionModel;
    SetIsBidder: (value: boolean) => IUserCompanyPermissionModel;
    SetIsGuest: (value: boolean) => IUserCompanyPermissionModel;
    SetUserId: (id: number) => IUserCompanyPermissionModel;
    SetModified: (value: Date) => IUserCompanyPermissionModel;
    ChangePermission: (key: PermissionsType) => IUserCompanyPermissionModel;
    CheckUserOfPermission: (userId?: number) => IUserCompanyPermissionModel;

    LoadFromAPI: (response: any) => IUserCompanyPermissionModel;
    LoadFromCached: (model: any) => IUserCompanyPermissionModel;
    HasOneValidPermission: () => boolean;
    ConvertFromView: (view: ICompanyUserRoleViewModel) => IUserCompanyPermissionModel;
    RevokeAccess: () => IUserCompanyPermissionModel;

    ToJson: () => string;
}

export default class UserCompanyPermissionModel implements IUserCompanyPermissionModel {
    Id: number;
    CompanyId: number;
    UserId: number;
    IsAdmin?: boolean;
    IsDirector?: boolean;
    IsBidder?: boolean;
    IsGuest?: boolean;
    Created?: Date;
    CreatedById: number;
    Modified?: Date;
    IsActive?: boolean;

    constructor(userLogged?: IUserModel, companyModel?: ICompanyModel) {
        this.Id = 0;
        this.CompanyId = companyModel?.Id || 0;
        this.UserId = 0;
        this.CreatedById = userLogged?.Id || 0;
        this.Created = this.Modified = moment().toDate();
        this.IsAdmin = this.IsDirector = this.IsBidder = this.IsGuest = false;
        this.IsActive = true;
    }

    //#region Setters

    SetIsActive = (value: boolean): IUserCompanyPermissionModel => {
        this.IsActive = value;
        return this;
    }

    SetIsAdmin = (value: boolean): IUserCompanyPermissionModel => {
        this.IsAdmin = value;
        return this;
    }

    SetIsBidder = (value: boolean): IUserCompanyPermissionModel => {
        this.IsBidder = value;
        return this;
    }

    SetIsDirector = (value: boolean): IUserCompanyPermissionModel => {
        this.IsDirector = value;
        return this;
    }

    SetIsGuest = (value: boolean): IUserCompanyPermissionModel => {
        this.IsGuest = value;
        return this;
    }

    SetUserId = (id: number): IUserCompanyPermissionModel => {
        this.UserId = id;
        return this;
    };

    SetModified = (value: Date): IUserCompanyPermissionModel => {
        this.Modified = value;
        return this;
    }

    //#endregion

    ChangePermission = (key: PermissionsType): IUserCompanyPermissionModel => {
        !this.IsActive && this.SetIsActive(true);

        this.SetIsAdmin(false);
        this.SetIsBidder(false);
        this.SetIsDirector(false);
        this.SetIsGuest(false);

        key == "Admin" && this.SetIsAdmin(true);
        key == "Director" && this.SetIsDirector(true);
        key == "Guest" && this.SetIsGuest(true);
        key == "Presenter" && this.SetIsBidder(true);

        return this;
    }

    CheckUserOfPermission = (userId?: number): IUserCompanyPermissionModel => {
        if (this.Id == 0 && this.UserId == 0 && (userId == null || !Number.isInteger(userId)))
            throw new Error("Permission without User.")

        if (this.Id == 0 && this.UserId == 0)
            this.SetUserId(userId!);

        return this;
    }

    HasOneValidPermission = (): boolean => {
        const result: boolean = this != null
            && this?.IsActive != null && this.IsActive
            && ((this.IsAdmin != null && this.IsAdmin)
                || (this.IsBidder != null && this.IsBidder)
                || (this.IsDirector != null && this.IsDirector)
                || (this.IsGuest != null && this.IsGuest));

        return result;
    };

    LoadFromAPI = (response: any): IUserCompanyPermissionModel => {
        const apiCreated: string = GetValue(response, "Created"),
            apiModified: string = GetValue(response, "Modified");

        this.Id = GetValue(response, "userCompanyPermissionId") as number;
        this.CompanyId = GetValue(response, "companyId") as number;
        this.UserId = GetValue(response, "userId") as number;
        this.Created = (apiCreated && IgnoreTimeZoneApiDate(apiCreated)) || this.Created;
        this.CreatedById = GetValue(response, "createdById") as number;
        this.Modified = (apiModified && IgnoreTimeZoneApiDate(apiModified)) || this.Modified;
        this.IsAdmin = GetValue(response, "isAdmin") as boolean;
        this.IsDirector = GetValue(response, "isDirector") as boolean;
        this.IsBidder = GetValue(response, "isBidder") as boolean;
        this.IsGuest = GetValue(response, "isGuest") as boolean;
        this.IsActive = GetValue(response, "isActive") as boolean;

        return this;
    }

    LoadFromCached = (model: any): IUserCompanyPermissionModel => {
        this.LoadFromAPI(model);
        this.Id = GetValue(model, "Id") as number;

        return this;
    }

    ConvertFromView = (view: ICompanyUserRoleViewModel): IUserCompanyPermissionModel => {

        if (view != null) {
            this.CompanyId = view.CompanyId;
            this.Created = view.Created;
            this.CreatedById = view.CreatedBy;
            this.Id = view.Id;
            this.IsActive = view.IsActive;
            this.IsAdmin = view.IsAdmin;
            this.IsBidder = view.IsBidder;
            this.IsDirector = view.IsDirector;
            this.IsGuest = view.IsGuest;
            this.Modified = view.Modified;
            this.UserId = view.UserId;
        }

        return this;
    }

    RevokeAccess = (): IUserCompanyPermissionModel => {
        this.IsActive = false;
        return this;
    }

    ToJson = (): string => {

        const dto = {
            userCompanyPermissionId: this.Id,
            companyId: this.CompanyId,
            userId: this.UserId,
            created: moment(this.Created).format(),
            createdById: this.CreatedById,
            modified: moment(this.Modified).format(),
            isAdmin: this.IsAdmin,
            isDirector: this.IsDirector,
            isBidder: this.IsBidder,
            isGuest: this.IsGuest,
            isActive: this.IsActive
        },
            result = JSON.stringify(dto);

        return result;
    };
}